import type { CSSInterpolation } from '@emotion/serialize';
import React, { forwardRef, use, useCallback, useRef, useState } from 'react';

import LinkDropdown from 'src/components/common/redesign-link-dropdown-cta/link-dropdown';
import { getDefaultButtonClassName } from 'src/components/facilities/buttonCss';
import ExpandIcon from 'src/components/icons/expand';
import HeaderUnderlay from 'src/components/layout/redesign/global-header-desktop/header-underlay';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import useOutsideClick from 'src/custom-hooks/use-outside-click';
import useWindowSize from 'src/custom-hooks/use-window-size';
import { borderRadius } from 'src/styling/constants';
import mq from 'src/styling/media-queries';

type RedesignLinkDropdownCtaProps = {
  text?: string | null;
  mobileText?: string | null;
  links?: readonly Queries.LinkDropdownCtaDropdownOptions[] | null;
  showingMobileNav?: boolean;
};

const createStyling = (styling: Styling, isMeridian: boolean, showingMobileNav?: boolean) => {
  const borderStyle = isMeridian
    ? `1px solid ${styling.colors.meridianOrange}`
    : `1px solid ${styling.colors.camelot}`;
  return {
    wrapper: mq({
      'height': 'auto',
      ...(showingMobileNav
        ? { display: 'flex', justifyContent: 'center', width: '100%' }
        : { display: 'inline', width: 'auto' }),
      'borderRadius': isMeridian
        ? borderRadius.redesignMeridianButton
        : borderRadius.redesignButton,
      'border': '1px solid transparent',
      'padding': '3px',
      '&:focus': {
        border: borderStyle,
      },
      '&:focus-within': {
        border: borderStyle,
      },
    }),
    expandIconWrapper: mq({ marginLeft: [0, '0.25rem'], opacity: '100%' }),
  } satisfies Record<string, CSSInterpolation>;
};

const RedesignLinkDropdownCta = forwardRef<HTMLDivElement, RedesignLinkDropdownCtaProps>(
  ({ text, links, showingMobileNav, mobileText }, ref) => {
    const { styling, isMeridian, buildType } = use(SiteContext);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const innerRef = (ref || dropdownRef) as React.MutableRefObject<HTMLDivElement | null>;
    const [showDropdown, setShowDropdown] = useState(false);
    const { isMobileHeader, isMobileOrSmaller } = useWindowSize();

    const componentStyling = createStyling(styling, isMeridian, showingMobileNav);

    const closeDropdown = useCallback(() => setShowDropdown(false), []);

    useOutsideClick(innerRef, closeDropdown);

    return (
      <div
        css={componentStyling.wrapper}
        ref={innerRef}
        onMouseEnter={() => !isMobileHeader && setShowDropdown(true)}
        onMouseLeave={() => !isMobileHeader && setShowDropdown(false)}
      >
        <button
          type="button"
          className={getDefaultButtonClassName(buildType)}
          css={[
            {
              svg: {
                marginLeft: 0,
              },
              minWidth: isMobileOrSmaller ? '6.25rem' : undefined,
            },
            showingMobileNav && { maxWidth: '30rem', width: '100%' },
            isMobileHeader &&
              mq({
                fontSize: ['1rem', '1.1875rem'],
                padding: [isMobileOrSmaller ? '0.75rem 0.5rem' : '0.75rem', '0.75rem 1.5rem'],
              }),
          ]}
          onClick={() => isMobileHeader && setShowDropdown(!showDropdown)}
        >
          <div className="flex items-center">
            <span>{isMobileOrSmaller ? mobileText : text}</span>
            <div css={componentStyling.expandIconWrapper}>
              <ExpandIcon
                size={isMobileOrSmaller ? 15 : 20}
                fill="white"
                isExpanded={showDropdown}
              />
            </div>
          </div>
        </button>
        {showDropdown && (
          <LinkDropdown
            links={links ?? []}
            showingMobileNav={showingMobileNav}
            onClick={closeDropdown}
          />
        )}
        {showDropdown && isMobileHeader && (
          <HeaderUnderlay
            tabIndex={0}
            onClick={closeDropdown}
            onFocus={closeDropdown}
            isAlwaysVisible
          />
        )}
      </div>
    );
  }
);

RedesignLinkDropdownCta.displayName = 'RedesignLinkDropdownCta';

export default RedesignLinkDropdownCta;
